import { useCallback, useEffect, useState } from 'react';
import ReferenceScreen from '../../components/reference/ReferenceScreen';
import FormAlert from '../../components/reference/FormAlert';
import ImportPanel from '../../components/requirements/ImportPanel';
import { apiErrorMessage, isNotFound } from '../../services/apiErrors';
import {
  currentMonth,
  formatTons,
  monthLabel,
  requirementsApi,
  shiftMonth,
  validTons,
} from '../../services/requirementsApi';
import type { RequirementChange, RequirementGrid, RequirementRow } from '../../services/requirementsApi';

const MONTHS_SHOWN = 12;
const STEP = 6;

const cellKey = (millId: string, ingredientId: string, month: string) => `${millId}|${ingredientId}|${month}`;

/** What a stored value looks like in its box: "1200.50" as "1200.5". */
const asTyped = (stored: string | undefined) => (stored === undefined ? '' : String(Number(stored)));

function describeChange(c: RequirementChange) {
  if (c.old_short_tons === null) return `set to ${formatTons(c.new_short_tons ?? 0)}`;
  if (c.new_short_tons === null) return `cleared (was ${formatTons(c.old_short_tons)})`;
  return `${formatTons(c.old_short_tons)} to ${formatTons(c.new_short_tons)}`;
}

/** How many short tons of each ingredient each mill needs in each month, with the total across mills. */
export default function RequirementsPage({ slug }: { slug: string }) {
  const [start, setStart] = useState(currentMonth);
  const [grid, setGrid] = useState<RequirementGrid | null>(null);
  const [loadError, setLoadError] = useState('');
  const [buyerMissing, setBuyerMissing] = useState(false);
  const [millFilter, setMillFilter] = useState('');
  // What has been typed and not yet saved, by cell.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState('');
  const [changes, setChanges] = useState<RequirementChange[] | null>(null);
  const [importing, setImporting] = useState(false);

  const loadHistory = useCallback(() => {
    requirementsApi
      .history(slug, { limit: 20 })
      .then(({ data }) => setChanges(data))
      .catch(() => setChanges(null));
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setGrid(null);
    setLoadError('');
    requirementsApi
      .grid(slug, start, MONTHS_SHOWN)
      .then(({ data }) => {
        if (!cancelled) setGrid(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isNotFound(err)) setBuyerMissing(true);
        else setLoadError('We could not load the requirements just now. Check your connection and try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug, start]);

  useEffect(loadHistory, [loadHistory]);

  const stored = (row: RequirementRow, month: string) => row.short_tons[month];
  const shown = (row: RequirementRow, month: string) => {
    const key = cellKey(row.mill_id, row.ingredient_id, month);
    return key in drafts ? drafts[key] : asTyped(stored(row, month));
  };

  // Only cells whose typed value differs from what is stored count as changes.
  const pending = (grid?.rows ?? []).flatMap((row) =>
    (grid?.months ?? []).flatMap((month) => {
      const key = cellKey(row.mill_id, row.ingredient_id, month);
      if (!(key in drafts)) return [];
      const typed = drafts[key].trim();
      const before = stored(row, month);
      const same = typed === '' ? before === undefined : before !== undefined && Number(typed) === Number(before);
      return same ? [] : [{ row, month, typed }];
    })
  );
  const invalid = pending.filter((p) => !validTons(p.typed));
  const pendingKeys = new Set(pending.map((p) => cellKey(p.row.mill_id, p.row.ingredient_id, p.month)));

  const type = (row: RequirementRow, month: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = cellKey(row.mill_id, row.ingredient_id, month);
    setDrafts((d) => ({ ...d, [key]: e.target.value }));
    setSaved('');
  };

  // Unsaved changes hold the window still, so nothing typed is lost out of sight.
  const move = (count: number) => setStart((s) => shiftMonth(s, count));

  const handleSave = async () => {
    if (!pending.length || invalid.length) return;
    setSaving(true);
    setSaveError('');
    setSaved('');
    const cells = pending.map((p) => ({
      mill_id: p.row.mill_id,
      ingredient_id: p.row.ingredient_id,
      month: p.month,
      short_tons: p.typed === '' ? null : p.typed,
    }));
    try {
      const { data } = await requirementsApi.save(slug, cells);
      setDrafts({});
      setSaved(data.changed === 1 ? '1 change saved.' : `${data.changed} changes saved.`);
    } catch (err) {
      setSaveError(apiErrorMessage(err, 'We could not save the requirements just now. Check your connection and try again.'));
      setSaving(false);
      return;
    }
    await reloadAfterSave();
    setSaving(false);
  };

  // Saved already, so a failed reload is a load problem, not a lost save.
  const reloadAfterSave = async () => {
    loadHistory();
    try {
      const { data: fresh } = await requirementsApi.grid(slug, start, MONTHS_SHOWN);
      setGrid(fresh);
    } catch {
      setLoadError('Your changes are saved, but we could not reload the requirements. Reload the page to see them.');
    }
  };

  const importSaved = async (changed: number) => {
    setImporting(false);
    setSaved(changed === 1 ? '1 change saved from the file.' : `${changed} changes saved from the file.`);
    await reloadAfterSave();
  };

  const mills = Array.from(new Map((grid?.rows ?? []).map((r) => [r.mill_id, r.mill_name])).entries());
  const rows = (grid?.rows ?? []).filter((r) => !millFilter || r.mill_id === millFilter);

  // Totals follow what is typed, so the effect of a change shows before it is saved.
  const totals = new Map<string, { name: string; byMonth: Record<string, number> }>();
  for (const row of rows) {
    const total = totals.get(row.ingredient_id) ?? { name: row.ingredient_name, byMonth: {} };
    for (const month of grid?.months ?? []) {
      const value = shown(row, month).trim();
      if (value !== '' && validTons(value)) total.byMonth[month] = (total.byMonth[month] ?? 0) + Number(value);
    }
    totals.set(row.ingredient_id, total);
  }
  const totalRows = Array.from(totals.values()).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <ReferenceScreen
      slug={slug}
      title="Requirements"
      lead={(buyer) =>
        `How many short tons of each ingredient each of ${buyer}'s mills needs in each month. Type in a box to change it, or import a spreadsheet; a blank box has no requirement.`
      }
      buyerMissing={buyerMissing}
    >
      <div className="mt-8 flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="rq-mill" className="label">
            Mill
          </label>
          <select id="rq-mill" value={millFilter} onChange={(e) => setMillFilter(e.target.value)} className="input">
            <option value="">All mills</option>
            {mills.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={() => move(-STEP)} disabled={saving || pending.length > 0}>
            Earlier
          </button>
          <button type="button" className="btn-secondary" onClick={() => move(STEP)} disabled={saving || pending.length > 0}>
            Later
          </button>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setImporting(true);
            setSaved('');
          }}
          // An import reloads the grid, which would drop what is typed and not saved.
          disabled={saving || importing || pending.length > 0}
        >
          Import
        </button>
        <div className="ml-auto flex items-center gap-3">
          {saved && (
            <p className="text-caption text-ink-muted" role="status">
              {saved}
            </p>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setDrafts({});
              setSaveError('');
            }}
            disabled={saving || !Object.keys(drafts).length}
          >
            Discard
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving || !pending.length || invalid.length > 0}>
            {saving ? 'Saving' : pending.length === 0 ? 'Save' : pending.length === 1 ? 'Save 1 change' : `Save ${pending.length} changes`}
          </button>
        </div>
      </div>

      {importing && (
        <ImportPanel
          slug={slug}
          start={start}
          months={MONTHS_SHOWN}
          onSaved={importSaved}
          onClose={() => setImporting(false)}
        />
      )}

      <div className="mt-4 space-y-3">
        <FormAlert lead="Requirements not saved." message={saveError} />
        {invalid.length > 0 && (
          <FormAlert
            lead="Check the boxes marked in red."
            message="Short tons must be a number of 0 or more, with no more than two decimals."
          />
        )}
        {loadError && <FormAlert lead="Requirements not loaded." message={loadError} />}
        {grid === null && !loadError && (
          <p className="text-body text-ink-muted" role="status">
            Loading requirements
          </p>
        )}
        {grid !== null && grid.rows.length === 0 && (
          <p className="text-body text-ink-muted">Add a mill and an ingredient first. Each pair gets a row here.</p>
        )}
      </div>

      {grid !== null && rows.length > 0 && (
        // The grid scrolls inside its own box; the mill and ingredient stay put on the left.
        <div className="relative mt-4 overflow-x-auto rounded-md border border-line bg-surface">
          <table className="border-collapse text-body">
            <caption className="sr-only">Monthly requirement in short tons</caption>
            <thead>
              <tr className="border-b border-line-strong text-left text-caption font-semibold text-ink-muted">
                <th scope="col" className="sticky left-0 z-10 min-w-[9rem] bg-surface px-3 py-2">
                  Mill
                </th>
                <th scope="col" className="min-w-[8rem] px-3 py-2">
                  Ingredient
                </th>
                {grid.months.map((m) => (
                  <th key={m} scope="col" className="px-2 py-2 text-right whitespace-nowrap">
                    {monthLabel(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.mill_id}|${row.ingredient_id}`} className="border-b border-line">
                  <th scope="row" className="sticky left-0 z-10 bg-surface px-3 py-1 text-left font-semibold text-ink">
                    {row.mill_name}
                  </th>
                  <td className="px-3 py-1 text-ink">
                    {row.ingredient_name}
                    {!row.active && <span className="ml-2 text-caption text-ink-muted">(switched off)</span>}
                  </td>
                  {grid.months.map((month) => {
                    const value = shown(row, month);
                    const key = cellKey(row.mill_id, row.ingredient_id, month);
                    const changed = pendingKeys.has(key);
                    const bad = changed && !validTons(value);
                    // A switched-off mill or ingredient takes no new requirement.
                    const locked = !row.active && stored(row, month) === undefined;
                    if (locked)
                      return (
                        <td key={month} className="px-3 py-1 text-right text-caption text-ink-muted">
                          <span aria-label={`${row.mill_name}, ${row.ingredient_name}, ${monthLabel(month)}: switched off`}>
                            Off
                          </span>
                        </td>
                      );
                    return (
                      <td key={month} className="px-1 py-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`${row.mill_name}, ${row.ingredient_name}, ${monthLabel(month)}, short tons`}
                          aria-invalid={bad || undefined}
                          value={value}
                          onChange={type(row, month)}
                          data-cell={key}
                          className={`w-20 rounded border px-2 py-1 text-right tabular-nums text-ink focus:outline-none focus:ring-2 focus:ring-navy ${
                            bad ? 'border-danger bg-danger-tint' : changed ? 'border-navy bg-navy-tint' : 'border-line'
                          }`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {totalRows.map((t) => (
                <tr key={t.name} className="border-t border-line-strong bg-paper">
                  <th scope="row" className="sticky left-0 z-10 bg-paper px-3 py-2 text-left text-caption font-semibold text-ink-muted">
                    {millFilter ? 'Total' : 'All mills'}
                  </th>
                  <td className="px-3 py-2 font-semibold text-ink">{t.name}</td>
                  {grid.months.map((m) => (
                    <td key={m} className="px-3 py-2 text-right font-semibold tabular-nums text-ink">
                      {t.byMonth[m] === undefined ? '' : formatTons(t.byMonth[m])}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      )}

      <section className="mt-12" aria-labelledby="rq-history">
        <h2 id="rq-history" className="text-heading text-ink">
          Recent changes
        </h2>
        {changes === null && <p className="mt-2 text-body text-ink-muted">The change history could not be loaded.</p>}
        {changes !== null && changes.length === 0 && <p className="mt-2 text-body text-ink-muted">No changes yet.</p>}
        {changes !== null && changes.length > 0 && (
          <ul className="mt-3 divide-y divide-line border-y border-line">
            {changes.map((c) => (
              <li key={c.id} className="flex flex-wrap justify-between gap-x-6 gap-y-1 py-2 text-body">
                <span className="text-ink">
                  <span className="font-semibold">{c.mill_name}</span>, {c.ingredient_name}, {monthLabel(c.month)}:{' '}
                  <span className="tabular-nums">{describeChange(c)}</span>
                </span>
                <span className="text-caption text-ink-muted">
                  {c.changed_by}, {new Date(c.changed_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                  {c.source === 'import' ? ', import' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </ReferenceScreen>
  );
}
